import {
  DokumentTypMappingEventTyp,
  unbekannteQuelldatenVariablen,
} from '@core/domain';
import type { DefaultValues } from 'react-hook-form';
import { z } from 'zod';

const MAX_BEILAGEN = 5;

export const versandartUebersteuerungOptions = ['E_MAIL', 'BRIEF'] as const;

export const emailVersandArtOptions = [
  'OHNE_DOKUMENT',
  'DOKUMENT',
  'DOKUMENT_ANHANG',
  'DOKUMENT_BRIEF',
  'DOKUMENT_ANHANG_BRIEF',
] as const;

export const beilageSchema = z
  .object({
    id: z.string().min(1, 'Beilage ID ist Pflicht.'),
    label: z.string().min(1, 'Beilage Label ist Pflicht.'),
    druck: z.boolean(),
    email: z.boolean(),
  })
  .refine((b) => b.druck || b.email, {
    message: 'Beilage muss mindestens für Druck oder E-Mail aktiviert sein.',
    path: ['druck'],
  });

export const dokumentTypErstellenSchema = z
  .object({
    dokumentTyp: z.string().trim().min(1).max(100),
    eventName: z.string().trim().min(1).max(100),
    eventTyp: DokumentTypMappingEventTyp,
    streamserveProjektExternalId: z.uuidv4(),
    exstreamServiceName: z.string().trim().max(100),
    versandartUebersteuerung: z
      .union([z.enum(versandartUebersteuerungOptions), z.literal(''), z.null()])
      .transform((v) => (v === '' ? null : v)),
    emailVersandArt: z.enum(emailVersandArtOptions),
    emailKundenkommunikation: z.boolean().nullable(),
    archivierungAktiv: z.boolean(),
    anmerkungAktiv: z.boolean(),
    anmerkungBetreff: z.string().trim().max(255),
    anmerkungText: z.string().trim().max(4000),
    prioVersand: z.boolean().optional(),
    beilagen: z.array(beilageSchema).max(MAX_BEILAGEN),
  })
  .superRefine((data, ctx) => {
    if (data.prioVersand && !data.exstreamServiceName) {
      ctx.addIssue({
        code: 'custom',
        path: ['exstreamServiceName'],
        message:
          'ExStream Servicename ist Pflicht, wenn Prioversand aktiv ist.',
      });
    }
    if (data.anmerkungAktiv && !data.anmerkungBetreff) {
      ctx.addIssue({
        code: 'custom',
        path: ['anmerkungBetreff'],
        message: 'Betreff ist Pflicht, wenn Anmerkungen aktiv sind.',
      });
    }
    if (data.anmerkungAktiv && !data.anmerkungText) {
      ctx.addIssue({
        code: 'custom',
        path: ['anmerkungText'],
        message: 'Anmerkungstext ist Pflicht, wenn Anmerkungen aktiv sind.',
      });
    }
    for (const feld of ['anmerkungBetreff', 'anmerkungText'] as const) {
      const unbekannt = data.anmerkungAktiv
        ? unbekannteQuelldatenVariablen(data[feld])
        : [];
      if (unbekannt.length > 0) {
        ctx.addIssue({
          code: 'custom',
          path: [feld],
          message: `Unbekannte Variable: ${unbekannt.map((v) => `\${${v}}`).join(', ')}`,
        });
      }
    }
  });

export type DokumentTypFormInput = z.input<typeof dokumentTypErstellenSchema>;
export type DokumentTypFormValues = z.output<typeof dokumentTypErstellenSchema>;

export const DEFAULT_DOKUMENTTYP_FORM_VALUES: DefaultValues<DokumentTypFormInput> =
  {
    dokumentTyp: '',
    eventName: '',
    eventTyp: 'order',
    streamserveProjektExternalId: '',
    exstreamServiceName: '',
    versandartUebersteuerung: '',
    emailVersandArt: 'OHNE_DOKUMENT',
    emailKundenkommunikation: null,
    archivierungAktiv: true,
    anmerkungAktiv: false,
    anmerkungBetreff: '',
    anmerkungText: '',
    prioVersand: false,
    beilagen: [],
  };
